import { useEffect, useRef } from "react";
import { X, Copy } from "lucide-react";

/**
 * Last-resort manual-copy modal.
 *
 * Shown when both navigator.clipboard.writeText() and the legacy
 * document.execCommand('copy') fallback fail. Renders the link in a
 * read-only input that auto-selects on focus so the user can do
 * Cmd/Ctrl+C themselves.
 */
export default function ManualCopyModal({ url, onClose }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!url) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    // Auto-select the URL when the modal opens.
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 60);
    return () => document.removeEventListener("keydown", onKey);
  }, [url, onClose]);

  if (!url) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="manual-copy-overlay"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-[#17171a] border border-[#2a2a30] p-6 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="manual-copy-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-md text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
          aria-label="Schließen"
          data-testid="manual-copy-close"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0 bg-[#1f1f23] text-[#ff6b35]">
            <Copy size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Bitte manuell kopieren</h2>
            <p className="text-sm text-[#9a9aa3] mt-1.5 leading-relaxed">
              Dein Browser hat das automatische Kopieren blockiert. Wähle den
              Link unten aus und drücke{" "}
              <kbd className="px-1.5 py-0.5 rounded bg-[#1f1f23] border border-[#2a2a30] text-[10px] font-mono">
                Cmd/Ctrl + C
              </kbd>
              .
            </p>
          </div>
        </div>

        <input
          ref={inputRef}
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          onClick={(e) => e.target.select()}
          className="mt-5 w-full h-11 px-3.5 rounded-lg bg-[#0a0a0b] border border-[#2a2a30] focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none text-white font-mono text-sm select-all"
          data-testid="manual-copy-input"
        />

        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center h-10 px-5 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold"
            data-testid="manual-copy-done"
          >
            Fertig
          </button>
        </div>
      </div>
    </div>
  );
}
